import type { ComponentProps } from 'react';
import classnames from 'classnames/bind';
import { get } from 'lodash/fp';
import { sprites } from './sprites';
import * as styles from './Icon.css';

const cx = classnames.bind(styles);

export type IconVariant = keyof typeof sprites;

export enum IconSize {
    EXTRA_TINY = 'extra-tiny',
    TINY = 'tiny',
    MINI = 'mini',
    EXTRA_SMALL = 'extra-small',
    SMALL = 'small',
    MEDIUM = 'medium',
    LARGE = 'large',
    EXTRA_LARGE = 'extra-large',
    SUPER_LARGE = 'super-large'
}

const sizeInPixels = {
    [IconSize.EXTRA_TINY]: 6,
    [IconSize.TINY]: 8,
    [IconSize.MINI]: 12,
    [IconSize.EXTRA_SMALL]: 16,
    [IconSize.SMALL]: 20,
    [IconSize.MEDIUM]: 24,
    [IconSize.LARGE]: 32,
    [IconSize.EXTRA_LARGE]: 48,
    [IconSize.SUPER_LARGE]: 60
} as const;

export interface Props extends ComponentProps<'svg'> {
    testId?: string;
    className?: string;
    title?: string;
    variant: IconVariant;
    size?: IconSize;
    inline?: boolean;
}

export function Icon({
    testId,
    className,
    title,
    variant,
    size = IconSize.MEDIUM,
    inline = false,
    ...props
}: Props) {
    const sprite = sprites[variant];
    const id = get('id', sprite);
    const viewBox = get('viewBox', sprite);
    const classes = cx('base', { inline }, className);
    const dimension = sizeInPixels[size];

    return (
        <svg
            role="img"
            aria-hidden="true"
            data-testid={testId}
            data-variant={variant}
            width={dimension}
            height={dimension}
            className={classes}
            viewBox={viewBox}
            {...props}>
            {title && <title>{title}</title>}
            <use xlinkHref={`#${id}`} />
        </svg>
    );
}
